import { Paper, Box, Container, Typography, Button as _LoadingButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { object, string, TypeOf } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormInput, useDynamicSnackbar } from '../share';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { useForgotPasswordMutation } from '../apis/Auth.api';
import {useTitle} from 'react-use';
import { Trans } from "@lingui/react/macro";
import { t, msg } from "@lingui/core/macro";

const LoadingButton = styled(_LoadingButton)`
  padding: 0.6rem 0;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
  }
`;

const handleError = (actionError: any, openFailSnackbar) => {
  if (Array.isArray(actionError?.data?.error)) {
    actionError.data.error.forEach((el: any) => {
      openFailSnackbar(el.message)
    });
  } else if (actionError?.data?.message) {
    openFailSnackbar(actionError.data.message);
  } else {
    console.log('Unknown error', actionError)
    openFailSnackbar(actionError.data.message);
  }
}

const forgotPasswordSchema = object({
    email: string()
        .trim()
        .min(1, msg`Email address is required`)
        .email(msg`Email Address is invalid`),
});

export type ForgotPasswordInput = TypeOf<typeof forgotPasswordSchema>;

const ForgotPasswordPage = () => {

    useTitle(t`Forgot Password`);
    
    const methods = useForm<ForgotPasswordInput>({
        resolver: zodResolver(forgotPasswordSchema),
    });

    // 👇 API Login Mutation
    const [forgotPassword, { isLoading, isError, error, isSuccess, data }] =
        useForgotPasswordMutation();

    const {
        reset,
        handleSubmit,
        formState: { isSubmitSuccessful },
    } = methods;

    const {openSuccessSnackbar, openFailSnackbar} = useDynamicSnackbar();

    useEffect(() => {
        if (isSuccess) {
          openSuccessSnackbar(data?.message);
        }
        if (isError) {
          handleError(error, openFailSnackbar)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitSuccessful]);

    const onSubmitHandler: SubmitHandler<ForgotPasswordInput> = async ({ email }) => {
        try {
          // 👇 Executing the forgotPassword Mutation
          const resp = await forgotPassword({ email });
          console.log("forgotPassword", resp)
        } catch (e) {
          handleError(e, openFailSnackbar)
        }
    };

    if (isSuccess) {
        return (
          <Paper sx={ { padding: "2em" } }>
            <Container
                maxWidth={false}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        maxWidth='27rem'
                        width='100%'
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: { xs: '1rem', sm: '2rem' },
                            borderRadius: 2,
                        }}
                    >
                        <Typography textAlign='center' component='h5'>
                            <Trans>We have sent you a password recovery email.</Trans>
                        </Typography>
                    </Box>
                </Box>
            </Container>
          </Paper>
        );
    }

    return (
      <Paper sx={ { padding: "2em" } }>
        <Container
            maxWidth={false}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <Typography
                  textAlign='center'
                  component='h2'
                  variant='h2'
                >
                    <Trans>Forgot Password</Trans>
                </Typography>
                <Typography
                    sx={{
                        fontSize: 15,
                        width: '100%',
                        textAlign: 'center',
                        mb: '1rem',
                    }}
                >
                    <Trans>Enter your email address and we’ll send you a link to reset your
                        password.</Trans>
                </Typography>

                <FormProvider {...methods}>
                    <Box
                        component='form'
                        onSubmit={handleSubmit(onSubmitHandler)}
                        noValidate
                        autoComplete='off'
                        maxWidth='27rem'
                        width='100%'
                        sx={{
                            p: { xs: '1rem', sm: '2rem' },
                            borderRadius: 2,
                        }}
                    >
                        <LoginFormInput name='email' label={t`Email Address`} type='email' />

                        <LoadingButton
                            variant='contained'
                            sx={{ mt: 1 }}
                            fullWidth
                            disableElevation
                            type='submit'
                            loading={isLoading}
                        >
                            <Trans>Retrieve Password</Trans>
                        </LoadingButton>

                        <Typography
                            sx={{ fontSize: '0.9rem', mt: '1rem', textAlign: 'center' }}
                        >
                            <Link to='/login'>
                                <Trans>Back to Login</Trans>
                            </Link>
                        </Typography>
                    </Box>
                </FormProvider>
            </Box>
        </Container>
      </Paper>
    );
};

export default ForgotPasswordPage;
